import {
  queryOptions as queryOptionsBuilder,
  useMutation,
  useQuery,
  useSuspenseQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult
} from '@tanstack/react-query';

import type {
  AuthChangePasswordRequest,
  AuthChangeUnverifiedEmailRequest,
  AuthConfirmEmailChangeRequest,
  AuthEmailLoginRequest,
  AuthEmailLoginResponse,
  AuthForgotPasswordRequest,
  AuthGoogleLoginRequest,
  AuthLoginResponse,
  AuthMeResponse,
  AuthMeUploadPictureRequest,
  AuthRegisterRequest,
  AuthRequestEmailChangeRequest,
  AuthResetPasswordRequest,
  AuthTwoFactorLoginRequest,
  AuthUpdateRequest,
  BiometricEnrollRequest,
  BiometricEnrollResponse,
  BiometricLoginRequest,
  TwoFactorCodeRequest,
  TwoFactorDisableRequest,
  TwoFactorRecoveryCodesResponse,
  TwoFactorSetupResponse
} from '../model';

import { customInstance } from '../../mutator/custom-instance-new.ts';
import type { ErrorType , BodyType } from '../../mutator/custom-instance-new.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Returns a session, or with two-factor authentication on, a token for `POST /auth/login/two-factor`.
 * @summary Login with email and password
 */
export const authLogin = (
    authEmailLoginRequest: BodyType<AuthEmailLoginRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthEmailLoginResponse>(
      {url: `/auth/login`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authEmailLoginRequest, signal
    },
      options);
    }




export const getAuthLoginMutationKey = () => ['authLogin'] as const;

export const getAuthLoginMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogin>>, TError,AuthLoginMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authLogin>>, TError,AuthLoginMutationVariables, TContext> => {

const mutationKey = getAuthLoginMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authLogin>>, AuthLoginMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authLogin(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthLoginMutationResult = NonNullable<Awaited<ReturnType<typeof authLogin>>>
    export type AuthLoginMutationBody = BodyType<AuthEmailLoginRequest>
    export type AuthLoginMutationError = ErrorType<void>
    export type AuthLoginMutationVariables = {data: BodyType<AuthEmailLoginRequest>}

    /**
 * @summary Login with email and password
 */
export const useAuthLogin = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogin>>, TError,AuthLoginMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authLogin>>,
        TError,
        AuthLoginMutationVariables,
        TContext
      > => {
      return useMutation(getAuthLoginMutationOptions(options), queryClient);
    }
    /**
 * Takes the token from `POST /auth/login` and a code from the authenticator app or a recovery code.
 * @summary Finish a password login with a two-factor code
 */
export const authLoginTwoFactor = (
    authTwoFactorLoginRequest: BodyType<AuthTwoFactorLoginRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/auth/login/two-factor`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authTwoFactorLoginRequest, signal
    },
      options);
    }




export const getAuthLoginTwoFactorMutationKey = () => ['authLoginTwoFactor'] as const;

export const getAuthLoginTwoFactorMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLoginTwoFactor>>, TError,AuthLoginTwoFactorMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authLoginTwoFactor>>, TError,AuthLoginTwoFactorMutationVariables, TContext> => {

const mutationKey = getAuthLoginTwoFactorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authLoginTwoFactor>>, AuthLoginTwoFactorMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authLoginTwoFactor(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthLoginTwoFactorMutationResult = NonNullable<Awaited<ReturnType<typeof authLoginTwoFactor>>>
    export type AuthLoginTwoFactorMutationBody = BodyType<AuthTwoFactorLoginRequest>
    export type AuthLoginTwoFactorMutationError = ErrorType<void>
    export type AuthLoginTwoFactorMutationVariables = {data: BodyType<AuthTwoFactorLoginRequest>}

    /**
 * @summary Finish a password login with a two-factor code
 */
export const useAuthLoginTwoFactor = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLoginTwoFactor>>, TError,AuthLoginTwoFactorMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authLoginTwoFactor>>,
        TError,
        AuthLoginTwoFactorMutationVariables,
        TContext
      > => {
      return useMutation(getAuthLoginTwoFactorMutationOptions(options), queryClient);
    }
    /**
 * Returns a session on both the solo and invited paths — registration now lands in the product rather than in the user's inbox. A solo signup is signed in but unverified, which blocks sending invites and the Assistant and nothing else.
 * @summary Register with email and password
 */
export const authRegister = (
    authRegisterRequest: BodyType<AuthRegisterRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/auth/register`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authRegisterRequest, signal
    },
      options);
    }




export const getAuthRegisterMutationKey = () => ['authRegister'] as const;

export const getAuthRegisterMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRegister>>, TError,AuthRegisterMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authRegister>>, TError,AuthRegisterMutationVariables, TContext> => {

const mutationKey = getAuthRegisterMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authRegister>>, AuthRegisterMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authRegister(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthRegisterMutationResult = NonNullable<Awaited<ReturnType<typeof authRegister>>>
    export type AuthRegisterMutationBody = BodyType<AuthRegisterRequest>
    export type AuthRegisterMutationError = ErrorType<void>
    export type AuthRegisterMutationVariables = {data: BodyType<AuthRegisterRequest>}

    /**
 * @summary Register with email and password
 */
export const useAuthRegister = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRegister>>, TError,AuthRegisterMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authRegister>>,
        TError,
        AuthRegisterMutationVariables,
        TContext
      > => {
      return useMutation(getAuthRegisterMutationOptions(options), queryClient);
    }
    /**
 * Consumes the code. It is single use and expires after 24h. Returns a session, so the link signs the user in on whichever device opened the mail.
 * @summary Confirm email registration
 */
export const authConfirmEmail = (
    code: string,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/auth/confirm/${code}`, method: 'POST', signal
    },
      options);
    }




export const getAuthConfirmEmailMutationKey = () => ['authConfirmEmail'] as const;

export const getAuthConfirmEmailMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authConfirmEmail>>, TError,AuthConfirmEmailMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authConfirmEmail>>, TError,AuthConfirmEmailMutationVariables, TContext> => {

const mutationKey = getAuthConfirmEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authConfirmEmail>>, AuthConfirmEmailMutationVariables> = (props) => {
          const {code} = props ?? {};

          return  authConfirmEmail(code,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthConfirmEmailMutationResult = NonNullable<Awaited<ReturnType<typeof authConfirmEmail>>>

    export type AuthConfirmEmailMutationError = ErrorType<void>
    export type AuthConfirmEmailMutationVariables = {code: string}

    /**
 * @summary Confirm email registration
 */
export const useAuthConfirmEmail = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authConfirmEmail>>, TError,AuthConfirmEmailMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authConfirmEmail>>,
        TError,
        AuthConfirmEmailMutationVariables,
        TContext
      > => {
      return useMutation(getAuthConfirmEmailMutationOptions(options), queryClient);
    }
    /**
 * Authenticated only, which is what keeps resend off the email-enumeration surface: an unverified user can always log in, so it never needs to be anonymous.
 * @summary Re-send the email confirmation link
 */
export const authResendVerification = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/resend-verification`, method: 'POST', signal
    },
      options);
    }




export const getAuthResendVerificationMutationKey = () => ['authResendVerification'] as const;

export const getAuthResendVerificationMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authResendVerification>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authResendVerification>>, TError,void, TContext> => {

const mutationKey = getAuthResendVerificationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authResendVerification>>, void> = () => {


          return  authResendVerification(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthResendVerificationMutationResult = NonNullable<Awaited<ReturnType<typeof authResendVerification>>>

    export type AuthResendVerificationMutationError = ErrorType<void>


    /**
 * @summary Re-send the email confirmation link
 */
export const useAuthResendVerification = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authResendVerification>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authResendVerification>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthResendVerificationMutationOptions(options), queryClient);
    }
    /**
 * The only route out of a mistyped address — resending cannot fix one. Rejected once the address is confirmed.
 * @summary Change the address while unverified and re-issue the code
 */
export const authChangeUnverifiedEmail = (
    authChangeUnverifiedEmailRequest: BodyType<AuthChangeUnverifiedEmailRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/unverified-email`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: authChangeUnverifiedEmailRequest, signal
    },
      options);
    }




export const getAuthChangeUnverifiedEmailMutationKey = () => ['authChangeUnverifiedEmail'] as const;

export const getAuthChangeUnverifiedEmailMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChangeUnverifiedEmail>>, TError,AuthChangeUnverifiedEmailMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authChangeUnverifiedEmail>>, TError,AuthChangeUnverifiedEmailMutationVariables, TContext> => {

const mutationKey = getAuthChangeUnverifiedEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authChangeUnverifiedEmail>>, AuthChangeUnverifiedEmailMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authChangeUnverifiedEmail(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthChangeUnverifiedEmailMutationResult = NonNullable<Awaited<ReturnType<typeof authChangeUnverifiedEmail>>>
    export type AuthChangeUnverifiedEmailMutationBody = BodyType<AuthChangeUnverifiedEmailRequest>
    export type AuthChangeUnverifiedEmailMutationError = ErrorType<void>
    export type AuthChangeUnverifiedEmailMutationVariables = {data: BodyType<AuthChangeUnverifiedEmailRequest>}

    /**
 * @summary Change the address while unverified and re-issue the code
 */
export const useAuthChangeUnverifiedEmail = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChangeUnverifiedEmail>>, TError,AuthChangeUnverifiedEmailMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authChangeUnverifiedEmail>>,
        TError,
        AuthChangeUnverifiedEmailMutationVariables,
        TContext
      > => {
      return useMutation(getAuthChangeUnverifiedEmailMutationOptions(options), queryClient);
    }
    /**
 * The code is valid for 10 minutes. `POST /auth/email-change/request` checks it.
 * @summary Send a code to the current address to start a change of email address
 */
export const authStartEmailChange = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/email-change/start`, method: 'POST', signal
    },
      options);
    }




export const getAuthStartEmailChangeMutationKey = () => ['authStartEmailChange'] as const;

export const getAuthStartEmailChangeMutationOptions = <TError = ErrorType<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authStartEmailChange>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authStartEmailChange>>, TError,void, TContext> => {

const mutationKey = getAuthStartEmailChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authStartEmailChange>>, void> = () => {


          return  authStartEmailChange(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthStartEmailChangeMutationResult = NonNullable<Awaited<ReturnType<typeof authStartEmailChange>>>

    export type AuthStartEmailChangeMutationError = ErrorType<unknown>


    /**
 * @summary Send a code to the current address to start a change of email address
 */
export const useAuthStartEmailChange = <TError = ErrorType<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authStartEmailChange>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authStartEmailChange>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthStartEmailChangeMutationOptions(options), queryClient);
    }
    /**
 * Takes the code sent to the current address (`POST /auth/email-change/start`) and emails a second code to the new address. A new request replaces any pending change.
 * @summary Request a change of a verified email address
 */
export const authRequestEmailChange = (
    authRequestEmailChangeRequest: BodyType<AuthRequestEmailChangeRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/email-change/request`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authRequestEmailChangeRequest, signal
    },
      options);
    }




export const getAuthRequestEmailChangeMutationKey = () => ['authRequestEmailChange'] as const;

export const getAuthRequestEmailChangeMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRequestEmailChange>>, TError,AuthRequestEmailChangeMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authRequestEmailChange>>, TError,AuthRequestEmailChangeMutationVariables, TContext> => {

const mutationKey = getAuthRequestEmailChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authRequestEmailChange>>, AuthRequestEmailChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authRequestEmailChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthRequestEmailChangeMutationResult = NonNullable<Awaited<ReturnType<typeof authRequestEmailChange>>>
    export type AuthRequestEmailChangeMutationBody = BodyType<AuthRequestEmailChangeRequest>
    export type AuthRequestEmailChangeMutationError = ErrorType<void>
    export type AuthRequestEmailChangeMutationVariables = {data: BodyType<AuthRequestEmailChangeRequest>}

    /**
 * @summary Request a change of a verified email address
 */
export const useAuthRequestEmailChange = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRequestEmailChange>>, TError,AuthRequestEmailChangeMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authRequestEmailChange>>,
        TError,
        AuthRequestEmailChangeMutationVariables,
        TContext
      > => {
      return useMutation(getAuthRequestEmailChangeMutationOptions(options), queryClient);
    }
    /**
 * Takes the code sent to the new address and swaps the address. Signs out every other session and revokes every biometric credential.
 * @summary Finish a change of email address
 */
export const authConfirmEmailChange = (
    authConfirmEmailChangeRequest: BodyType<AuthConfirmEmailChangeRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/email-change/confirm`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authConfirmEmailChangeRequest, signal
    },
      options);
    }




export const getAuthConfirmEmailChangeMutationKey = () => ['authConfirmEmailChange'] as const;

export const getAuthConfirmEmailChangeMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authConfirmEmailChange>>, TError,AuthConfirmEmailChangeMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authConfirmEmailChange>>, TError,AuthConfirmEmailChangeMutationVariables, TContext> => {

const mutationKey = getAuthConfirmEmailChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authConfirmEmailChange>>, AuthConfirmEmailChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authConfirmEmailChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthConfirmEmailChangeMutationResult = NonNullable<Awaited<ReturnType<typeof authConfirmEmailChange>>>
    export type AuthConfirmEmailChangeMutationBody = BodyType<AuthConfirmEmailChangeRequest>
    export type AuthConfirmEmailChangeMutationError = ErrorType<void>
    export type AuthConfirmEmailChangeMutationVariables = {data: BodyType<AuthConfirmEmailChangeRequest>}

    /**
 * @summary Finish a change of email address
 */
export const useAuthConfirmEmailChange = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authConfirmEmailChange>>, TError,AuthConfirmEmailChangeMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authConfirmEmailChange>>,
        TError,
        AuthConfirmEmailChangeMutationVariables,
        TContext
      > => {
      return useMutation(getAuthConfirmEmailChangeMutationOptions(options), queryClient);
    }
    /**
 * @summary Refresh the JWT token
 */
export const authRefresh = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/auth/refresh`, method: 'POST', signal
    },
      options);
    }




export const getAuthRefreshMutationKey = () => ['authRefresh'] as const;

export const getAuthRefreshMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRefresh>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authRefresh>>, TError,void, TContext> => {

const mutationKey = getAuthRefreshMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authRefresh>>, void> = () => {


          return  authRefresh(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthRefreshMutationResult = NonNullable<Awaited<ReturnType<typeof authRefresh>>>

    export type AuthRefreshMutationError = ErrorType<void>


    /**
 * @summary Refresh the JWT token
 */
export const useAuthRefresh = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRefresh>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authRefresh>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthRefreshMutationOptions(options), queryClient);
    }
    /**
 * @summary End this session by revoking its refresh token
 */
export const authLogout = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/logout`, method: 'POST', signal
    },
      options);
    }




export const getAuthLogoutMutationKey = () => ['authLogout'] as const;

export const getAuthLogoutMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogout>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authLogout>>, TError,void, TContext> => {

const mutationKey = getAuthLogoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authLogout>>, void> = () => {


          return  authLogout(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthLogoutMutationResult = NonNullable<Awaited<ReturnType<typeof authLogout>>>

    export type AuthLogoutMutationError = ErrorType<void>


    /**
 * @summary End this session by revoking its refresh token
 */
export const useAuthLogout = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLogout>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authLogout>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthLogoutMutationOptions(options), queryClient);
    }
    /**
 * @summary Request password reset with MFA code
 */
export const authForgotPassword = (
    authForgotPasswordRequest: BodyType<AuthForgotPasswordRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/forgot-password`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authForgotPasswordRequest, signal
    },
      options);
    }




export const getAuthForgotPasswordMutationKey = () => ['authForgotPassword'] as const;

export const getAuthForgotPasswordMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authForgotPassword>>, TError,AuthForgotPasswordMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authForgotPassword>>, TError,AuthForgotPasswordMutationVariables, TContext> => {

const mutationKey = getAuthForgotPasswordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authForgotPassword>>, AuthForgotPasswordMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authForgotPassword(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthForgotPasswordMutationResult = NonNullable<Awaited<ReturnType<typeof authForgotPassword>>>
    export type AuthForgotPasswordMutationBody = BodyType<AuthForgotPasswordRequest>
    export type AuthForgotPasswordMutationError = ErrorType<void>
    export type AuthForgotPasswordMutationVariables = {data: BodyType<AuthForgotPasswordRequest>}

    /**
 * @summary Request password reset with MFA code
 */
export const useAuthForgotPassword = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authForgotPassword>>, TError,AuthForgotPasswordMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authForgotPassword>>,
        TError,
        AuthForgotPasswordMutationVariables,
        TContext
      > => {
      return useMutation(getAuthForgotPasswordMutationOptions(options), queryClient);
    }
    /**
 * @summary Reset password with MFA code
 */
export const authResetPassword = (
    authResetPasswordRequest: BodyType<AuthResetPasswordRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/reset-password`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authResetPasswordRequest, signal
    },
      options);
    }




export const getAuthResetPasswordMutationKey = () => ['authResetPassword'] as const;

export const getAuthResetPasswordMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authResetPassword>>, TError,AuthResetPasswordMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authResetPassword>>, TError,AuthResetPasswordMutationVariables, TContext> => {

const mutationKey = getAuthResetPasswordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authResetPassword>>, AuthResetPasswordMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authResetPassword(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthResetPasswordMutationResult = NonNullable<Awaited<ReturnType<typeof authResetPassword>>>
    export type AuthResetPasswordMutationBody = BodyType<AuthResetPasswordRequest>
    export type AuthResetPasswordMutationError = ErrorType<void>
    export type AuthResetPasswordMutationVariables = {data: BodyType<AuthResetPasswordRequest>}

    /**
 * @summary Reset password with MFA code
 */
export const useAuthResetPassword = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authResetPassword>>, TError,AuthResetPasswordMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authResetPassword>>,
        TError,
        AuthResetPasswordMutationVariables,
        TContext
      > => {
      return useMutation(getAuthResetPasswordMutationOptions(options), queryClient);
    }
    /**
 * @summary Change password with MFA code for authenticated user
 */
export const authChangePassword = (
    authChangePasswordRequest: BodyType<AuthChangePasswordRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/change-password`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authChangePasswordRequest, signal
    },
      options);
    }




export const getAuthChangePasswordMutationKey = () => ['authChangePassword'] as const;

export const getAuthChangePasswordMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChangePassword>>, TError,AuthChangePasswordMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authChangePassword>>, TError,AuthChangePasswordMutationVariables, TContext> => {

const mutationKey = getAuthChangePasswordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authChangePassword>>, AuthChangePasswordMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authChangePassword(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthChangePasswordMutationResult = NonNullable<Awaited<ReturnType<typeof authChangePassword>>>
    export type AuthChangePasswordMutationBody = BodyType<AuthChangePasswordRequest>
    export type AuthChangePasswordMutationError = ErrorType<void>
    export type AuthChangePasswordMutationVariables = {data: BodyType<AuthChangePasswordRequest>}

    /**
 * @summary Change password with MFA code for authenticated user
 */
export const useAuthChangePassword = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChangePassword>>, TError,AuthChangePasswordMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authChangePassword>>,
        TError,
        AuthChangePasswordMutationVariables,
        TContext
      > => {
      return useMutation(getAuthChangePasswordMutationOptions(options), queryClient);
    }
    /**
 * @summary Unlink a social auth provider
 */
export const authUnlinkSocial = (
    provider: string,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/social/${provider}`, method: 'DELETE', signal
    },
      options);
    }




export const getAuthUnlinkSocialMutationKey = () => ['authUnlinkSocial'] as const;

export const getAuthUnlinkSocialMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authUnlinkSocial>>, TError,AuthUnlinkSocialMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authUnlinkSocial>>, TError,AuthUnlinkSocialMutationVariables, TContext> => {

const mutationKey = getAuthUnlinkSocialMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authUnlinkSocial>>, AuthUnlinkSocialMutationVariables> = (props) => {
          const {provider} = props ?? {};

          return  authUnlinkSocial(provider,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthUnlinkSocialMutationResult = NonNullable<Awaited<ReturnType<typeof authUnlinkSocial>>>

    export type AuthUnlinkSocialMutationError = ErrorType<void>
    export type AuthUnlinkSocialMutationVariables = {provider: string}

    /**
 * @summary Unlink a social auth provider
 */
export const useAuthUnlinkSocial = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authUnlinkSocial>>, TError,AuthUnlinkSocialMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authUnlinkSocial>>,
        TError,
        AuthUnlinkSocialMutationVariables,
        TContext
      > => {
      return useMutation(getAuthUnlinkSocialMutationOptions(options), queryClient);
    }
    /**
 * With two-factor authentication on, `twoFactorCode` is required.
 * @summary Issue a biometric sign-in credential for this device
 */
export const authEnrollBiometric = (
    biometricEnrollRequest: BodyType<BiometricEnrollRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<BiometricEnrollResponse>(
      {url: `/auth/biometric/enroll`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biometricEnrollRequest, signal
    },
      options);
    }




export const getAuthEnrollBiometricMutationKey = () => ['authEnrollBiometric'] as const;

export const getAuthEnrollBiometricMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authEnrollBiometric>>, TError,AuthEnrollBiometricMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authEnrollBiometric>>, TError,AuthEnrollBiometricMutationVariables, TContext> => {

const mutationKey = getAuthEnrollBiometricMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authEnrollBiometric>>, AuthEnrollBiometricMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authEnrollBiometric(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthEnrollBiometricMutationResult = NonNullable<Awaited<ReturnType<typeof authEnrollBiometric>>>
    export type AuthEnrollBiometricMutationBody = BodyType<BiometricEnrollRequest>
    export type AuthEnrollBiometricMutationError = ErrorType<void>
    export type AuthEnrollBiometricMutationVariables = {data: BodyType<BiometricEnrollRequest>}

    /**
 * @summary Issue a biometric sign-in credential for this device
 */
export const useAuthEnrollBiometric = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authEnrollBiometric>>, TError,AuthEnrollBiometricMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authEnrollBiometric>>,
        TError,
        AuthEnrollBiometricMutationVariables,
        TContext
      > => {
      return useMutation(getAuthEnrollBiometricMutationOptions(options), queryClient);
    }
    /**
 * @summary Login with a biometric credential
 */
export const authLoginBiometric = (
    biometricLoginRequest: BodyType<BiometricLoginRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/auth/biometric/login`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biometricLoginRequest, signal
    },
      options);
    }




export const getAuthLoginBiometricMutationKey = () => ['authLoginBiometric'] as const;

export const getAuthLoginBiometricMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLoginBiometric>>, TError,AuthLoginBiometricMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authLoginBiometric>>, TError,AuthLoginBiometricMutationVariables, TContext> => {

const mutationKey = getAuthLoginBiometricMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authLoginBiometric>>, AuthLoginBiometricMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authLoginBiometric(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthLoginBiometricMutationResult = NonNullable<Awaited<ReturnType<typeof authLoginBiometric>>>
    export type AuthLoginBiometricMutationBody = BodyType<BiometricLoginRequest>
    export type AuthLoginBiometricMutationError = ErrorType<void>
    export type AuthLoginBiometricMutationVariables = {data: BodyType<BiometricLoginRequest>}

    /**
 * @summary Login with a biometric credential
 */
export const useAuthLoginBiometric = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLoginBiometric>>, TError,AuthLoginBiometricMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authLoginBiometric>>,
        TError,
        AuthLoginBiometricMutationVariables,
        TContext
      > => {
      return useMutation(getAuthLoginBiometricMutationOptions(options), queryClient);
    }
    /**
 * @summary Revoke a biometric credential of the current user
 */
export const authRevokeBiometric = (
    credentialId: string,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/biometric/${credentialId}`, method: 'DELETE', signal
    },
      options);
    }




export const getAuthRevokeBiometricMutationKey = () => ['authRevokeBiometric'] as const;

export const getAuthRevokeBiometricMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRevokeBiometric>>, TError,AuthRevokeBiometricMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authRevokeBiometric>>, TError,AuthRevokeBiometricMutationVariables, TContext> => {

const mutationKey = getAuthRevokeBiometricMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authRevokeBiometric>>, AuthRevokeBiometricMutationVariables> = (props) => {
          const {credentialId} = props ?? {};

          return  authRevokeBiometric(credentialId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthRevokeBiometricMutationResult = NonNullable<Awaited<ReturnType<typeof authRevokeBiometric>>>

    export type AuthRevokeBiometricMutationError = ErrorType<void>
    export type AuthRevokeBiometricMutationVariables = {credentialId: string}

    /**
 * @summary Revoke a biometric credential of the current user
 */
export const useAuthRevokeBiometric = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authRevokeBiometric>>, TError,AuthRevokeBiometricMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authRevokeBiometric>>,
        TError,
        AuthRevokeBiometricMutationVariables,
        TContext
      > => {
      return useMutation(getAuthRevokeBiometricMutationOptions(options), queryClient);
    }
    /**
 * @summary Login with Google
 */
export const authLoginGoogle = (
    authGoogleLoginRequest: BodyType<AuthGoogleLoginRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/auth/google/login`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: authGoogleLoginRequest, signal
    },
      options);
    }




export const getAuthLoginGoogleMutationKey = () => ['authLoginGoogle'] as const;

export const getAuthLoginGoogleMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLoginGoogle>>, TError,AuthLoginGoogleMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authLoginGoogle>>, TError,AuthLoginGoogleMutationVariables, TContext> => {

const mutationKey = getAuthLoginGoogleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authLoginGoogle>>, AuthLoginGoogleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authLoginGoogle(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthLoginGoogleMutationResult = NonNullable<Awaited<ReturnType<typeof authLoginGoogle>>>
    export type AuthLoginGoogleMutationBody = BodyType<AuthGoogleLoginRequest>
    export type AuthLoginGoogleMutationError = ErrorType<void>
    export type AuthLoginGoogleMutationVariables = {data: BodyType<AuthGoogleLoginRequest>}

    /**
 * @summary Login with Google
 */
export const useAuthLoginGoogle = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authLoginGoogle>>, TError,AuthLoginGoogleMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authLoginGoogle>>,
        TError,
        AuthLoginGoogleMutationVariables,
        TContext
      > => {
      return useMutation(getAuthLoginGoogleMutationOptions(options), queryClient);
    }
    /**
 * @summary Get the current user
 */
export const authMe = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthMeResponse>(
      {url: `/auth/me`, method: 'GET', signal
    },
      options);
    }




export const getAuthMeQueryKey = () => {
    return [
    `/auth/me`
    ] as const;
    }


export const getAuthMeQueryOptions = <TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAuthMeQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof authMe>>> = ({ signal }) => authMe(requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AuthMeQueryResult = NonNullable<Awaited<ReturnType<typeof authMe>>>
export type AuthMeQueryError = ErrorType<void>


export function useAuthMe<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof authMe>>,
          TError,
          Awaited<ReturnType<typeof authMe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthMe<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof authMe>>,
          TError,
          Awaited<ReturnType<typeof authMe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthMe<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the current user
 */

export function useAuthMe<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAuthMeQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAuthMeSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>( options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAuthMeQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof authMe>>> = ({ signal }) => authMe(requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type AuthMeSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof authMe>>>
export type AuthMeSuspenseQueryError = ErrorType<void>


export function useAuthMeSuspense<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthMeSuspense<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAuthMeSuspense<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the current user
 */

export function useAuthMeSuspense<TData = Awaited<ReturnType<typeof authMe>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof authMe>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAuthMeSuspenseQueryOptions(options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * @summary Update the current user
 */
export const authUpdate = (
    authUpdateRequest: BodyType<AuthUpdateRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthMeResponse>(
      {url: `/auth/me`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: authUpdateRequest, signal
    },
      options);
    }




export const getAuthUpdateMutationKey = () => ['authUpdate'] as const;

export const getAuthUpdateMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authUpdate>>, TError,AuthUpdateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authUpdate>>, TError,AuthUpdateMutationVariables, TContext> => {

const mutationKey = getAuthUpdateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authUpdate>>, AuthUpdateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authUpdate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof authUpdate>>>
    export type AuthUpdateMutationBody = BodyType<AuthUpdateRequest>
    export type AuthUpdateMutationError = ErrorType<void>
    export type AuthUpdateMutationVariables = {data: BodyType<AuthUpdateRequest>}

    /**
 * @summary Update the current user
 */
export const useAuthUpdate = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authUpdate>>, TError,AuthUpdateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authUpdate>>,
        TError,
        AuthUpdateMutationVariables,
        TContext
      > => {
      return useMutation(getAuthUpdateMutationOptions(options), queryClient);
    }
    /**
 * @summary Upload a profile picture
 */
export const authUploadProfilePicture = (
    authMeUploadPictureRequest: BodyType<AuthMeUploadPictureRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {

      const formData = new FormData();
formData.append(`file`, authMeUploadPictureRequest.file);

      return customInstance<void>(
      {url: `/auth/me/picture`, method: 'POST',
      headers: {'Content-Type': 'multipart/form-data', },
       data: formData, signal
    },
      options);
    }




export const getAuthUploadProfilePictureMutationKey = () => ['authUploadProfilePicture'] as const;

export const getAuthUploadProfilePictureMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authUploadProfilePicture>>, TError,AuthUploadProfilePictureMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authUploadProfilePicture>>, TError,AuthUploadProfilePictureMutationVariables, TContext> => {

const mutationKey = getAuthUploadProfilePictureMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authUploadProfilePicture>>, AuthUploadProfilePictureMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  authUploadProfilePicture(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthUploadProfilePictureMutationResult = NonNullable<Awaited<ReturnType<typeof authUploadProfilePicture>>>
    export type AuthUploadProfilePictureMutationBody = BodyType<AuthMeUploadPictureRequest>
    export type AuthUploadProfilePictureMutationError = ErrorType<void>
    export type AuthUploadProfilePictureMutationVariables = {data: BodyType<AuthMeUploadPictureRequest>}

    /**
 * @summary Upload a profile picture
 */
export const useAuthUploadProfilePicture = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authUploadProfilePicture>>, TError,AuthUploadProfilePictureMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authUploadProfilePicture>>,
        TError,
        AuthUploadProfilePictureMutationVariables,
        TContext
      > => {
      return useMutation(getAuthUploadProfilePictureMutationOptions(options), queryClient);
    }
    /**
 * @summary Delete the profile picture
 */
export const authDeleteProfilePicture = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/me/picture`, method: 'DELETE', signal
    },
      options);
    }




export const getAuthDeleteProfilePictureMutationKey = () => ['authDeleteProfilePicture'] as const;

export const getAuthDeleteProfilePictureMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authDeleteProfilePicture>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authDeleteProfilePicture>>, TError,void, TContext> => {

const mutationKey = getAuthDeleteProfilePictureMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authDeleteProfilePicture>>, void> = () => {


          return  authDeleteProfilePicture(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthDeleteProfilePictureMutationResult = NonNullable<Awaited<ReturnType<typeof authDeleteProfilePicture>>>

    export type AuthDeleteProfilePictureMutationError = ErrorType<void>


    /**
 * @summary Delete the profile picture
 */
export const useAuthDeleteProfilePicture = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authDeleteProfilePicture>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authDeleteProfilePicture>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getAuthDeleteProfilePictureMutationOptions(options), queryClient);
    }
    /**
 * @summary Change the current user group
 */
export const authChangeGroup = (
    id: number,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/auth/change-group/${id}`, method: 'PATCH', signal
    },
      options);
    }




export const getAuthChangeGroupMutationKey = () => ['authChangeGroup'] as const;

export const getAuthChangeGroupMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChangeGroup>>, TError,AuthChangeGroupMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof authChangeGroup>>, TError,AuthChangeGroupMutationVariables, TContext> => {

const mutationKey = getAuthChangeGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof authChangeGroup>>, AuthChangeGroupMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  authChangeGroup(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AuthChangeGroupMutationResult = NonNullable<Awaited<ReturnType<typeof authChangeGroup>>>

    export type AuthChangeGroupMutationError = ErrorType<void>
    export type AuthChangeGroupMutationVariables = {id: number}

    /**
 * @summary Change the current user group
 */
export const useAuthChangeGroup = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof authChangeGroup>>, TError,AuthChangeGroupMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof authChangeGroup>>,
        TError,
        AuthChangeGroupMutationVariables,
        TContext
      > => {
      return useMutation(getAuthChangeGroupMutationOptions(options), queryClient);
    }
    /**
 * Creates a new secret for the authenticator app. It does nothing until `POST /auth/two-factor/enable` confirms it, and it replaces any earlier unfinished setup.
 * @summary Start setting up two-factor authentication
 */
export const twoFactorSetup = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<TwoFactorSetupResponse>(
      {url: `/auth/two-factor/setup`, method: 'POST', signal
    },
      options);
    }




export const getTwoFactorSetupMutationKey = () => ['twoFactorSetup'] as const;

export const getTwoFactorSetupMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorSetup>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof twoFactorSetup>>, TError,void, TContext> => {

const mutationKey = getTwoFactorSetupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof twoFactorSetup>>, void> = () => {


          return  twoFactorSetup(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TwoFactorSetupMutationResult = NonNullable<Awaited<ReturnType<typeof twoFactorSetup>>>

    export type TwoFactorSetupMutationError = ErrorType<void>


    /**
 * @summary Start setting up two-factor authentication
 */
export const useTwoFactorSetup = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorSetup>>, TError,void, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof twoFactorSetup>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getTwoFactorSetupMutationOptions(options), queryClient);
    }
    /**
 * Takes a code from the authenticator app. Returns the recovery codes once. Signs out every other session and revokes every biometric credential.
 * @summary Turn on two-factor authentication
 */
export const twoFactorEnable = (
    twoFactorCodeRequest: BodyType<TwoFactorCodeRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<TwoFactorRecoveryCodesResponse>(
      {url: `/auth/two-factor/enable`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorCodeRequest, signal
    },
      options);
    }




export const getTwoFactorEnableMutationKey = () => ['twoFactorEnable'] as const;

export const getTwoFactorEnableMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorEnable>>, TError,TwoFactorEnableMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof twoFactorEnable>>, TError,TwoFactorEnableMutationVariables, TContext> => {

const mutationKey = getTwoFactorEnableMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof twoFactorEnable>>, TwoFactorEnableMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  twoFactorEnable(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TwoFactorEnableMutationResult = NonNullable<Awaited<ReturnType<typeof twoFactorEnable>>>
    export type TwoFactorEnableMutationBody = BodyType<TwoFactorCodeRequest>
    export type TwoFactorEnableMutationError = ErrorType<void>
    export type TwoFactorEnableMutationVariables = {data: BodyType<TwoFactorCodeRequest>}

    /**
 * @summary Turn on two-factor authentication
 */
export const useTwoFactorEnable = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorEnable>>, TError,TwoFactorEnableMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof twoFactorEnable>>,
        TError,
        TwoFactorEnableMutationVariables,
        TContext
      > => {
      return useMutation(getTwoFactorEnableMutationOptions(options), queryClient);
    }
    /**
 * The old recovery codes stop working. Returns the new ones once.
 * @summary Replace the recovery codes
 */
export const twoFactorRegenerateRecoveryCodes = (
    twoFactorCodeRequest: BodyType<TwoFactorCodeRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<TwoFactorRecoveryCodesResponse>(
      {url: `/auth/two-factor/recovery-codes`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorCodeRequest, signal
    },
      options);
    }




export const getTwoFactorRegenerateRecoveryCodesMutationKey = () => ['twoFactorRegenerateRecoveryCodes'] as const;

export const getTwoFactorRegenerateRecoveryCodesMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorRegenerateRecoveryCodes>>, TError,TwoFactorRegenerateRecoveryCodesMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof twoFactorRegenerateRecoveryCodes>>, TError,TwoFactorRegenerateRecoveryCodesMutationVariables, TContext> => {

const mutationKey = getTwoFactorRegenerateRecoveryCodesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof twoFactorRegenerateRecoveryCodes>>, TwoFactorRegenerateRecoveryCodesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  twoFactorRegenerateRecoveryCodes(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TwoFactorRegenerateRecoveryCodesMutationResult = NonNullable<Awaited<ReturnType<typeof twoFactorRegenerateRecoveryCodes>>>
    export type TwoFactorRegenerateRecoveryCodesMutationBody = BodyType<TwoFactorCodeRequest>
    export type TwoFactorRegenerateRecoveryCodesMutationError = ErrorType<void>
    export type TwoFactorRegenerateRecoveryCodesMutationVariables = {data: BodyType<TwoFactorCodeRequest>}

    /**
 * @summary Replace the recovery codes
 */
export const useTwoFactorRegenerateRecoveryCodes = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorRegenerateRecoveryCodes>>, TError,TwoFactorRegenerateRecoveryCodesMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof twoFactorRegenerateRecoveryCodes>>,
        TError,
        TwoFactorRegenerateRecoveryCodesMutationVariables,
        TContext
      > => {
      return useMutation(getTwoFactorRegenerateRecoveryCodesMutationOptions(options), queryClient);
    }
    /**
 * @summary Turn off two-factor authentication
 */
export const twoFactorDisable = (
    twoFactorDisableRequest: BodyType<TwoFactorDisableRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/auth/two-factor/disable`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorDisableRequest, signal
    },
      options);
    }




export const getTwoFactorDisableMutationKey = () => ['twoFactorDisable'] as const;

export const getTwoFactorDisableMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorDisable>>, TError,TwoFactorDisableMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof twoFactorDisable>>, TError,TwoFactorDisableMutationVariables, TContext> => {

const mutationKey = getTwoFactorDisableMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof twoFactorDisable>>, TwoFactorDisableMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  twoFactorDisable(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TwoFactorDisableMutationResult = NonNullable<Awaited<ReturnType<typeof twoFactorDisable>>>
    export type TwoFactorDisableMutationBody = BodyType<TwoFactorDisableRequest>
    export type TwoFactorDisableMutationError = ErrorType<void>
    export type TwoFactorDisableMutationVariables = {data: BodyType<TwoFactorDisableRequest>}

    /**
 * @summary Turn off two-factor authentication
 */
export const useTwoFactorDisable = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof twoFactorDisable>>, TError,TwoFactorDisableMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof twoFactorDisable>>,
        TError,
        TwoFactorDisableMutationVariables,
        TContext
      > => {
      return useMutation(getTwoFactorDisableMutationOptions(options), queryClient);
    }
